import { Link, useRouter, useSearch } from '@tanstack/react-router';
import { CopyToClipboard, Page } from '@uturn/portal/components';
import { ShipmentSearchResult } from '@uturn/portal/components/header/global-search/shipment-search-result';
import { getShipmentSearchResultPrices } from '@uturn/portal/components/header/global-search/shipment-search-result-prices';
import {
	InfoRouteCard,
	InfoRouteCardContent,
	InfoRouteCardHeader,
	InfoRouteCardLabel,
	InfoRouteCardRoutes,
} from '@uturn/portal/components/info-route-card';
import BreakDownTooltip, {
	formatShipmentPrice,
	formatShipmentPriceLabel,
} from '@uturn/portal/components/price-breakdown-tooltip';
import { ShipmentStatusBadge } from '@uturn/portal/modules/shipments/components/shipment-status-badge';
import { getRouteDate } from '@uturn/portal/utils/get-route-date';
import { PaginationCompact } from '@uturn/ui-kit';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
	useInstantSearch,
	usePagination,
	useSearchBox,
} from 'react-instantsearch';

export function SearchResultsOverview() {
	const { refine } = useSearchBox();
	const { t } = useTranslation();
	const query = useSearch({ from: '/_protected/search' });

	const { results } = useInstantSearch();
	const router = useRouter();
	const pagination = usePagination();

	useEffect(() => {
		const paramQuery = query.q;

		if (paramQuery !== undefined && results.query !== paramQuery) {
			refine(paramQuery);
		}
	}, []);

	return (
		<>
			<Helmet
				title={
					results.nbPages > 1 && results.hits.length === results.hitsPerPage
						? t(
								'pages.search.paginated.title',
								'{{amount}}+ search results for "{{query}}"',
								{
									amount: results.hits.length,
									query: results.query,
								},
							).toString()
						: t(
								'pages.search.title',
								'{{amount}} search results for "{{query}}"',
								{
									amount: results.hits.length,
									query: results.query,
								},
							).toString()
				}
			/>
			<Page
				title={
					results.nbPages > 1 && results.hits.length === results.hitsPerPage
						? t(
								'pages.search.paginated.title',
								'{{amount}}+ search results for "{{query}}"',
								{
									amount: results.hits.length,
									query: results.query,
								},
							).toString()
						: t(
								'pages.search.title',
								'{{amount}} search results for "{{query}}"',
								{
									amount: results.hits.length,
									query: results.query,
								},
							).toString()
				}
				className="m-auto md:max-w-[850px]"
			>
				<div className="grid gap-3">
					{results.hits.map((result: ShipmentSearchResult) => {
						const { isShipmentMatched, price, feeValue, totalPrice } =
							getShipmentSearchResultPrices(result);

						return (
							<Link
								key={result.id}
								to="/shipments/$shipmentNumber"
								params={{ shipmentNumber: result.shipmentNumber }}
							>
								<InfoRouteCard>
									<InfoRouteCardHeader>
										<div className="z-10 flex justify-start gap-3.5 font-light text-muted-foreground">
											<span className="z-10">
												{t(
													'pages.search.card.title',
													'Shipment ID - %{{ ID }}',
													{
														ID: result.shipmentNumber,
													},
												)}
												<CopyToClipboard
													value={result.shipmentNumber.toString()}
												/>
											</span>

											<ShipmentStatusBadge status={result.shipmentStatus} />
										</div>
									</InfoRouteCardHeader>
									<InfoRouteCardContent
										routes={
											<InfoRouteCardRoutes
												routes={result.locations.map((location: any) => ({
													name: location.name,
													description: (
														<span>
															{getRouteDate(
																location.dateFrom?.replace('Z', ''),
																location.dateUntil?.replace('Z', ''),
															)}
														</span>
													),
												}))}
												compact={false}
											/>
										}
									>
										{result.invoiceReference && (
											<InfoRouteCardLabel
												title={t('pages.search.card.reference', 'Reference')}
											>
												{result.invoiceReference}
											</InfoRouteCardLabel>
										)}

										<InfoRouteCardLabel
											title={
												isShipmentMatched
													? t(
															'breakdown_tooltip.agreed_total_price.label',
															'Agreed total price',
														)
													: t(
															'breakdown_tooltip.target_price.label',
															'Total price',
														)
											}
										>
											{formatShipmentPriceLabel(totalPrice)}
											<BreakDownTooltip
												isShipmentMatched={isShipmentMatched}
												price={formatShipmentPrice(price)}
												feeValue={formatShipmentPrice(feeValue)}
												totalPrice={formatShipmentPrice(totalPrice)}
											/>
										</InfoRouteCardLabel>
										{result.carrierName && (
											<InfoRouteCardLabel
												title={t('pages.search.card.carrier', 'Carrier')}
											>
												{result.carrierName}
											</InfoRouteCardLabel>
										)}
									</InfoRouteCardContent>
								</InfoRouteCard>
							</Link>
						);
					})}
				</div>
				<PaginationCompact
					totalPages={results.nbPages}
					page={results.page}
					setPage={pagination.refine}
				/>
			</Page>
		</>
	);
}
